import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Calendar, Clock, CheckCircle, AlertTriangle, Play, 
  RotateCcw, Package, Settings, ChevronRight, BarChart3,
  Loader2, RefreshCw, Filter, TrendingUp, DollarSign, Activity,
  Shield, Sliders
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue 
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { toast } from 'sonner';
import { cycleCountHeaders, cycleCountPath } from './cycleCountContext';

const API = process.env.REACT_APP_BACKEND_URL;

const getHeaders = cycleCountHeaders;

// ABC class color mappings
const abcColors = {
  A: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  B: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  C: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
};

// Status badge colors
const statusColors = {
  SCHEDULED: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  IN_PROGRESS: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  COMPLETED: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  CANCELLED: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
};

// Classification mode descriptions
const modeDescriptions = {
  VALUE: { label: 'By Value', icon: DollarSign, desc: 'High inventory value = A' },
  USAGE: { label: 'By Usage', icon: TrendingUp, desc: 'High consumption = A' },
  HYBRID_MAX: { label: 'Hybrid Max', icon: Activity, desc: 'High value OR usage = A' },
  MANUAL: { label: 'Manual', icon: Settings, desc: 'Set manually per item' }
};

export default function CycleCountPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [scheduling, setScheduling] = useState(false);
  const [recomputing, setRecomputing] = useState(false);
  const [savingRules, setSavingRules] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [rules, setRules] = useState(null);
  const [activeTab, setActiveTab] = useState('overdue');
  const [selectedMode, setSelectedMode] = useState('VALUE');
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Load dashboard and tasks
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [dashRes, tasksRes] = await Promise.all([
        fetch(`${API}/api/inventory/cycle-count/dashboard`, { headers: getHeaders() }),
        fetch(`${API}/api/inventory/cycle-count/tasks`, { headers: getHeaders() })
      ]);
      
      if (!dashRes.ok || !tasksRes.ok) {
        throw new Error('Failed to load data');
      }
      
      const [dashData, tasksData] = await Promise.all([dashRes.json(), tasksRes.json()]);
      setDashboard(dashData);
      setTasks(tasksData.tasks || []);
    } catch (err) {
      console.error('Load error:', err);
      toast.error('Failed to load cycle count data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Listen for branch changes
  useEffect(() => {
    const handleBranchChange = () => {
      loadData();
    };
    window.addEventListener('branch-changed', handleBranchChange);
    return () => window.removeEventListener('branch-changed', handleBranchChange);
  }, [loadData]);

  // Schedule new counts
  const handleSchedule = async () => {
    try {
      setScheduling(true);
      const res = await fetch(`${API}/api/inventory/cycle-count/schedule`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ horizonDays: 30 })
      });
      
      if (!res.ok) throw new Error('Schedule failed');
      
      const data = await res.json();
      toast.success(`Created ${data.tasks_created} new count tasks`);
      loadData();
    } catch (err) {
      toast.error('Failed to schedule counts');
    } finally {
      setScheduling(false);
    }
  };

  // Recompute ABC classification
  const handleRecompute = async () => {
    try {
      setRecomputing(true);
      const res = await fetch(`${API}/api/inventory/abc/recompute`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ mode: selectedMode })
      });
      
      if (!res.ok) throw new Error('Recompute failed');
      
      const data = await res.json();
      const modeLabel = modeDescriptions[selectedMode]?.label || selectedMode;
      toast.success(`Classified ${data.result.classified} items by ${modeLabel}: A=${data.result.a_count}, B=${data.result.b_count}, C=${data.result.c_count}`);
      loadData();
    } catch (err) {
      toast.error('Failed to recompute ABC classification');
    } finally {
      setRecomputing(false);
    }
  };

  // Filter tasks by tab
  const today = new Date().toISOString().split('T')[0];
  const weekAhead = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  
  const filteredTasks = tasks.filter(task => {
    if (activeTab === 'overdue') {
      return task.is_overdue || (task.status === 'IN_PROGRESS');
    } else if (activeTab === 'upcoming') {
      return !task.is_overdue && task.status === 'SCHEDULED' && task.due_date <= weekAhead;
    } else if (activeTab === 'completed') {
      return task.status === 'COMPLETED';
    }
    return true;
  });

  // Start a task
  const handleStartTask = async (taskId) => {
    try {
      const res = await fetch(`${API}/api/inventory/cycle-count/tasks/${taskId}/start`, {
        method: 'POST',
        headers: getHeaders()
      });
      
      if (!res.ok) throw new Error('Start failed');
      
      toast.success('Count started');
      navigate(cycleCountPath(taskId));
    } catch (err) {
      toast.error('Failed to start count');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <img src="/chain29-logo.png" alt="Loading" className="w-12 h-12 rounded-xl object-cover animate-pulse" />
      </div>
    );
  }

  const currentSource = dashboard?.abc_summary?.latest_source || 'Not classified';

  return (
    <div className="space-y-6" data-testid="cycle-count-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold dark:text-[#d6deeb]">ABC Cycle Counting</h1>
          <p className="text-muted-foreground">
            Automated inventory counts by ABC classification
            {currentSource !== 'Not classified' && (
              <Badge variant="outline" className="ml-2 text-xs">
                Current: {modeDescriptions[currentSource]?.label || currentSource}
              </Badge>
            )}
          </p>
        </div>
        <div className="flex gap-2 items-center">
          {/* Mode Selector */}
          <Select value={selectedMode} onValueChange={setSelectedMode}>
            <SelectTrigger className="w-[160px] dark:bg-[#1d3b53] dark:border-[#2d4a63]" data-testid="mode-selector">
              <SelectValue placeholder="Select mode" />
            </SelectTrigger>
            <SelectContent className="dark:bg-[#0b2942] dark:border-[#1d3b53]">
              {Object.entries(modeDescriptions).map(([mode, info]) => (
                <SelectItem key={mode} value={mode} className="dark:hover:bg-[#1d3b53]">
                  <div className="flex items-center gap-2">
                    <info.icon className="h-4 w-4" />
                    <span>{info.label}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Button 
            variant="outline" 
            onClick={handleRecompute}
            disabled={recomputing || selectedMode === 'MANUAL'}
            data-testid="recompute-abc-btn"
          >
            {recomputing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <BarChart3 className="h-4 w-4 mr-2" />}
            Recompute
          </Button>
          <Button 
            onClick={handleSchedule}
            disabled={scheduling}
            data-testid="schedule-counts-btn"
          >
            {scheduling ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Calendar className="h-4 w-4 mr-2" />}
            Schedule Counts
          </Button>
        </div>
      </div>

      {/* Dashboard Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="dark:bg-[#0b2942] dark:border-[#1d3b53]">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Overdue</p>
                <p className="text-2xl font-bold text-red-600 dark:text-red-400">{dashboard?.overdue_count || 0}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-red-500 opacity-80" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="dark:bg-[#0b2942] dark:border-[#1d3b53]">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Due This Week</p>
                <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{dashboard?.due_soon_count || 0}</p>
              </div>
              <Clock className="h-8 w-8 text-yellow-500 opacity-80" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="dark:bg-[#0b2942] dark:border-[#1d3b53]">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">In Progress</p>
                <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">{dashboard?.in_progress_count || 0}</p>
              </div>
              <Play className="h-8 w-8 text-purple-500 opacity-80" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="dark:bg-[#0b2942] dark:border-[#1d3b53]">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Completed (Month)</p>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">{dashboard?.completed_this_month || 0}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-500 opacity-80" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ABC Summary */}
      <Card className="dark:bg-[#0b2942] dark:border-[#1d3b53]">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg dark:text-[#d6deeb] flex items-center gap-2">
              <Package className="h-5 w-5" />
              ABC Classification
            </CardTitle>
            {currentSource && currentSource !== 'Not classified' && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                {(() => {
                  const ModeIcon = modeDescriptions[currentSource]?.icon || Settings;
                  return <ModeIcon className="h-4 w-4" />;
                })()}
                <span>{modeDescriptions[currentSource]?.desc || currentSource}</span>
                {dashboard?.abc_summary?.rules?.usageLookbackDays && currentSource !== 'VALUE' && currentSource !== 'MANUAL' && (
                  <Badge variant="outline" className="text-xs">
                    {dashboard.abc_summary.rules.usageLookbackDays}d lookback
                  </Badge>
                )}
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50">
              <div className="text-3xl font-bold text-red-600 dark:text-red-400">
                {dashboard?.abc_summary?.a_count || 0}
              </div>
              <div className="text-sm text-red-700 dark:text-red-300">A Items</div>
              <div className="text-xs text-muted-foreground mt-1">Weekly counts</div>
            </div>
            <div className="text-center p-4 rounded-lg bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800/50">
              <div className="text-3xl font-bold text-yellow-600 dark:text-yellow-400">
                {dashboard?.abc_summary?.b_count || 0}
              </div>
              <div className="text-sm text-yellow-700 dark:text-yellow-300">B Items</div>
              <div className="text-xs text-muted-foreground mt-1">Monthly counts</div>
            </div>
            <div className="text-center p-4 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/50">
              <div className="text-3xl font-bold text-green-600 dark:text-green-400">
                {dashboard?.abc_summary?.c_count || 0}
              </div>
              <div className="text-sm text-green-700 dark:text-green-300">C Items</div>
              <div className="text-xs text-muted-foreground mt-1">Quarterly counts</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tasks List */}
      <Card className="dark:bg-[#0b2942] dark:border-[#1d3b53]">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg dark:text-[#d6deeb]">Count Tasks</CardTitle>
            <Button variant="ghost" size="icon" onClick={loadData} data-testid="refresh-tasks-btn">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-4 dark:bg-[#1d3b53]">
              <TabsTrigger value="overdue" className="data-[state=active]:dark:bg-[#0b2942]">
                Overdue & Active
                {(dashboard?.overdue_count > 0 || dashboard?.in_progress_count > 0) && (
                  <Badge variant="destructive" className="ml-2">
                    {(dashboard?.overdue_count || 0) + (dashboard?.in_progress_count || 0)}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="upcoming" className="data-[state=active]:dark:bg-[#0b2942]">
                Due Soon
              </TabsTrigger>
              <TabsTrigger value="completed" className="data-[state=active]:dark:bg-[#0b2942]">
                Completed
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value={activeTab}>
              {filteredTasks.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Package className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No tasks in this category</p>
                  {activeTab !== 'completed' && (
                    <Button variant="link" onClick={handleSchedule} className="mt-2">
                      Schedule new counts
                    </Button>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredTasks.map(task => (
                    <div 
                      key={task.id}
                      className="flex items-center justify-between p-4 rounded-lg border dark:border-[#1d3b53] hover:bg-muted/50 dark:hover:bg-[#1d3b53]/50 transition-colors cursor-pointer"
                      onClick={() => task.status === 'SCHEDULED' ? handleStartTask(task.id) : navigate(cycleCountPath(task.id))}
                      data-testid={`task-item-${task.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <Badge className={abcColors[task.abc_class]}>
                          {task.abc_class}
                        </Badge>
                        <div>
                          <div className="font-medium dark:text-[#d6deeb]">{task.task_number}</div>
                          <div className="text-sm text-muted-foreground flex items-center gap-2">
                            <Calendar className="h-3 w-3" />
                            {task.due_date}
                            {task.is_overdue && (
                              <Badge variant="destructive" className="text-xs">Overdue</Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <div className="text-sm text-muted-foreground">
                            {task.items_counted}/{task.total_items} items
                          </div>
                          {task.status === 'COMPLETED' && task.items_with_variance > 0 && (
                            <div className="text-xs text-yellow-600 dark:text-yellow-400">
                              {task.items_with_variance} variances
                            </div>
                          )}
                        </div>
                        <Badge className={statusColors[task.status]}>
                          {task.status.replace('_', ' ')}
                        </Badge>
                        <ChevronRight className="h-5 w-5 text-muted-foreground" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
